import React from 'react';
import type { JSX } from 'react';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import './ToggleButtonGroup.scss';

interface ToggleButtonGroupProps {
  /** Available options — values can be strings or ReactNode for rich/responsive labels */
  options: Record<string, React.ReactNode>;
  /** Currently selected values */
  value: string[];
  /** Fires with the full updated selection array */
  onChange: (value: string[]) => void;
  /** Button variant */
  variant?: 'primary' | 'secondary';
  /** Padding and font density tier */
  density?: 'compact' | 'default' | 'spacious';
  /** Hover/active transform intensity */
  motionScale?: 'sm' | 'base' | 'lg';
  /** Optional additional CSS class */
  className?: string;
  disabled?: boolean;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
}

/**
 * Multi-select toggle button group — visual counterpart of RadioButton,
 * but allows zero or more items to be active simultaneously.
 */
const ToggleButtonGroup: React.FC<ToggleButtonGroupProps> = ({
  options,
  value,
  onChange,
  className = '',
  variant = 'primary',
  density = 'default',
  motionScale = 'base',
  disabled = false,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
}): JSX.Element => {
  return (
    <ToggleGroup.Root
      type="multiple"
      value={value}
      onValueChange={onChange}
      disabled={disabled}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      className={`toggle-button-group ${className}`}
    >
      {Object.entries(options).map(([key, label]) => (
        <ToggleGroup.Item
          key={key}
          value={key}
          className={`toggle-button-group__item toggle-button-group__item--${density} toggle-button-group__item--${variant} toggle-button-group__item--linear toggle-button-group__item--motion-${motionScale}`}
        >
          {label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
};

export default ToggleButtonGroup;
